import React from 'react';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const avatarVariants = cva(
  'relative inline-flex items-center justify-center rounded-full overflow-hidden transition-all duration-300',
  {
    variants: {
      size: {
        xs: 'w-6 h-6 text-[10px]',
        sm: 'w-8 h-8 text-xs',
        md: 'w-10 h-10 text-sm',
        lg: 'w-12 h-12 text-base',
        xl: 'w-16 h-16 text-lg',
        '2xl': 'w-20 h-20 text-xl',
      },
      ring: {
        none: '',
        primary: 'ring-2 ring-primary ring-offset-2 ring-offset-background',
        secondary: 'ring-2 ring-muted ring-offset-2 ring-offset-background',
        glow: 'ring-2 ring-primary/50 ring-offset-2 ring-offset-background shadow-lg shadow-primary/20',
      },
    },
    defaultVariants: {
      size: 'md',
      ring: 'none',
    },
  }
);

const statusIndicatorVariants = cva(
  'absolute rounded-full border-2 border-background transition-all duration-300',
  {
    variants: {
      status: {
        online: 'bg-success',
        offline: 'bg-muted-foreground',
        away: 'bg-warning',
        busy: 'bg-destructive',
      },
      size: {
        xs: 'w-2 h-2 bottom-0 right-0',
        sm: 'w-2.5 h-2.5 bottom-0 right-0',
        md: 'w-3 h-3 bottom-0 right-0',
        lg: 'w-3.5 h-3.5 bottom-0 right-0',
        xl: 'w-4 h-4 bottom-0 right-0',
        '2xl': 'w-5 h-5 bottom-0 right-0',
      },
    },
    defaultVariants: {
      status: 'offline',
      size: 'md',
    },
  }
);

export interface CinematicAvatarProps extends VariantProps<typeof avatarVariants> {
  src?: string;
  fallback: string;
  status?: 'online' | 'offline' | 'away' | 'busy';
  glow?: boolean;
  className?: string;
  alt?: string;
}

export const CinematicAvatar = React.forwardRef<HTMLDivElement, CinematicAvatarProps>(
  ({ src, fallback, size, status, ring, glow, className, alt }, ref) => {
    const [imageError, setImageError] = React.useState(false);

    const getInitials = (name: string) => {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

    return (
      <div
        ref={ref}
        className={cn(
          avatarVariants({ size, ring: ring === 'glow' ? 'glow' : ring }),
          glow && 'hover:shadow-xl hover:shadow-primary/30 hover:scale-105',
          className
        )}
      >
        {src && !imageError ? (
          <img
            src={src}
            alt={alt || fallback}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className={cn(
            'w-full h-full flex items-center justify-center font-semibold',
            'bg-gradient-to-br from-muted to-muted/50 text-muted-foreground'
          )}>
            {getInitials(fallback)}
          </div>
        )}
        
        {status && (
          <span
            className={cn(
              statusIndicatorVariants({ status, size }),
              status === 'online' && 'animate-pulse'
            )}
          />
        )}
      </div>
    );
  }
);

CinematicAvatar.displayName = 'CinematicAvatar';

export default CinematicAvatar;
